import { Component, OnInit, AfterViewInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { lastValueFrom } from 'rxjs';
import { TimeoutableComponent } from 'src/shared/components/timeoutable/timeoutable.component';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { StaticData } from 'src/providers/static-data/static-data';
import { TeamService } from 'src/providers/team/team';
import { Communication } from 'src/providers/communication/communication';
import { NotificationsApiService, NotificationSetting } from 'src/shared/services/notifications-api.service';

@Component({
    templateUrl: 'notification-settings.html',
    styleUrls: ['notification-settings.scss'],
    standalone: false
})

export class NotificationSettingsPage extends TimeoutableComponent implements OnInit, AfterViewInit {

  backPage: string;
  allNotificationSettings: any;
  filteredNotificationSettings: any;
  totalNotifications: number;
  errorMessage: string;
  isRightSideContent = true;
  searchForm: FormGroup;
  queryNotificationsSettings: any[];
  lastSavedSettings: any;
  savingText: string;
  expandedIndex: string | null = null;
  timeOpts = [];
  smsForwardingText: any;

  constructor(
    private notificationsApi: NotificationsApiService,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
    private formBuilder: FormBuilder,
    private staticData: StaticData,
    private teamService: TeamService,
    public communication: Communication
  ) {
    super();
    this.searchForm = this.formBuilder.group({
      search: [''],
    });
  }

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.rightSidePanelService.setDialogPageTitle('Notification Settings');
    this.timeOpts = this.staticData.selectTimeOpts();
    this.loadNotificationSettings();
  }

  async ngAfterViewInit() {
    this.watchSearchForm();
  }

  async loadNotificationSettings() {
    try {
      const [notificationSettings, smsForwardingText] = await Promise.all([
        lastValueFrom(this.notificationsApi.getNotificationSettings()),
        this.teamService.getSMSForwardingText('notification-settings')
      ]);
      this.smsForwardingText = smsForwardingText;
      this.allNotificationSettings = this.parseNotificationsSettings(notificationSettings);
      this.initializeTimeRangeForms();
      this.watchTimeRangeForms();
      this.filteredNotificationSettings = this.allNotificationSettings;
      this.totalNotifications = this.allNotificationSettings.length;
      this.loaded = true;
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  parseNotificationsSettings(notificationsSettings: any[]) {
    return notificationsSettings.map((setting) => ({
      ...setting,
      title: setting.title.replaceAll('_', ' ').replaceAll('.', ' '),
      channels: this.parseChannels(
        setting.active_channels,
        setting.available_channels
      ),
      allowsTimeRangeConfig: setting.possible_settings?.includes(
        'allowed_time_ranges'
      ),
    }));
  }

  parseChannels(activeChannels: any[], availableChannels: any[]) {
    return availableChannels.map((channel) => ({
      name: channel.replaceAll('_', ' ').replaceAll('.', ' '),
      checked: activeChannels.includes(channel),
      isOpened: false,
    }));
  }

  openChannelSettings(channel: any) {
    channel.isOpened = !channel.isOpened;
  }

  initializeTimeRangeForms() {
    const daysOfWeek = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ];
    this.allNotificationSettings.forEach((notification) => {
      notification.channels.forEach((channel) => {
        channel.timeRangeForms = daysOfWeek.map((day) => {
          const channelName = this.addUnderScore(channel.name);
          const weekDay = this.getWeekDay(day);
          const foundTimeRangeSetting = notification?.allowed_time_ranges?.find(
            (range) =>
              range.week_day === weekDay && range.channel === channelName
          );
          const isChannelEnabled = notification?.active_channels?.includes(
            channel.name
          );
          const isDayChecked =
            isChannelEnabled &&
            (notification?.allowed_time_ranges === null ||
              !foundTimeRangeSetting ||
              foundTimeRangeSetting?.ranges?.length > 0);
          return {
            dayForm: this.formBuilder.group({
              day: [day],
              checked: [isDayChecked],
            }),
            timeRangeForm: this.getPatchedTimeRangeForm(
              foundTimeRangeSetting?.ranges
            ),
          };
        });
      });
    });
  }

  private getWeekDay(day: string): number {
    const daysOfWeek = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ];
    return daysOfWeek.indexOf(day);
  }

  getPatchedTimeRangeForm(ranges: any[]) {
    if (!ranges) {
      return this.formBuilder.array([]);
    }
    return this.formBuilder.array(
      ranges.map((range) => {
        const startTime = range.split('-')[0];
        const endTime = range.split('-')[1];
        return this.formBuilder.group({
          start_time: [startTime],
          end_time: [endTime],
        });
      })
    );
  }

  addUnderScore(channel: string) {
    return channel.replace(' ', '_');
  }

  watchSearchForm() {
    this.searchForm.valueChanges.subscribe((value) => {
      if (value === '' || value === null) {
        this.filteredNotificationSettings = this.allNotificationSettings;
        return;
      }
      this.queryNotificationsSettings = this.allNotificationSettings.filter(
        (notification) =>
          notification.title.toLowerCase().includes(value.search.toLowerCase())
      );
      this.filteredNotificationSettings = this.queryNotificationsSettings;
    });
  }

  watchTimeRangeForms() {
    this.allNotificationSettings.forEach((notification) => {
      notification.channels.forEach((channel) => {
        channel.timeRangeForms.forEach((form) => {
          form.timeRangeForm.valueChanges
            .pipe(debounceTime(300))
            .subscribe((value) => {
              const isTimeRangesValid = value.every(
                (range) => range.start_time !== '' && range.end_time !== ''
              );
              if (isTimeRangesValid) {
                this.updateNotificationsSettingsArray();
                const payload = this.buildSettingsUpdatePayload(
                  notification.notification_type_id
                );
                this.saveSettings(payload);
              }
            });
        });
      });
    });
  }

  updateNotificationsSettingsArray() {
    try {
      this.allNotificationSettings.forEach((originalNotification) => {
        const filteredNotification = this.filteredNotificationSettings.find(
          (notification) =>
            notification.notification_type_id ===
            originalNotification.notification_type_id
        );

        if (filteredNotification) {
          originalNotification.channels.forEach((channel, index) => {
            const filteredChannel = filteredNotification.channels[index];
            channel.checked = filteredChannel.checked;
            channel.timeRangeForms.forEach((form, formIndex) => {
              const filteredForm = filteredChannel.timeRangeForms[formIndex];
              form.dayForm.controls.checked.setValue(
                filteredForm.dayForm.controls.checked.value
              );
            });
          });
        }
      });
    } catch (_error) {
      // Intentionally empty - errors handled in called methods
    }
  }

  buildSettingsUpdatePayload(notificationTypeId: number) {
    try {
      const notification = this.allNotificationSettings.find(
        (n) => n.notification_type_id === notificationTypeId
      );
      const availableChannels = notification.channels
        .filter((channel) => channel.checked)
        .map((channel) => channel.name.replace(' ', '_'));
      const payload = {
        notification_type_id: notificationTypeId,
        settings: {
          channels: availableChannels,
          allowed_time_ranges: this.getParsedAllowedTimeRanges(
            notification.channels
          ),
        },
      };
      return payload;
    } catch (_error) {
      // Intentionally empty - errors handled in called methods
    }
  }

  private getParsedAllowedTimeRanges(channels: any[]) {
    const filteredChannels = channels.filter((channel) => channel.checked);
    return filteredChannels
      .map((channel) =>
        channel.timeRangeForms.map((form) => ({
          week_day: this.getWeekDay(form.dayForm.controls.day.value),
          ranges: this.getParsedRanges(
            form.timeRangeForm.value,
            form.dayForm.value.checked
          ),
          channel: channel.name.replaceAll(' ', '_'),
        }))
      )
      .flat();
  }

  private getParsedRanges(ranges: any[], enabled: boolean) {
    if (enabled === false) {
      return [];
    }
    if (!ranges || ranges.length === 0) {
      return null;
    }
    return ranges.map((range) => `${range.start_time}-${range.end_time}`);
  }

  async saveSettings(payload: NotificationSetting) {
    try {
      if (JSON.stringify(payload) !== JSON.stringify(this.lastSavedSettings)) {
        this.savingText = 'Saving changes..';

        await lastValueFrom(this.notificationsApi.updateNotificationSetting(payload));
        this.lastSavedSettings = payload;
        setTimeout(() => {
          this.savingText = 'All changes saved';
        }, 1000);
      }
    } catch (_error) {
      // Intentionally empty - errors handled in called methods
    }
  }

  onAccordionChange(index: number, timeRangeIndex: number) {
    if (this.expandedIndex === `${index  }-${  timeRangeIndex}`) {
      this.expandedIndex = null;
    } else {
      this.expandedIndex = `${index  }-${  timeRangeIndex}`;
    }
  }

  toggleDay(event: any, notification: any, channel: any, formGrp: any) {
    event.stopPropagation();

    setTimeout(() => {
      channel.timeRangeForms.forEach((form) => {
        if (
          form.dayForm.controls.day.value === formGrp.dayForm.controls.day.value
        ) {
          form.dayForm.controls.checked.value =
            form.dayForm.controls.checked.value;
          form.dayForm.updateValueAndValidity();
        }
      });
      const isAllDaysUnchecked = channel.timeRangeForms.every(
        (form) => !form.dayForm.controls.checked.value
      );
      if (isAllDaysUnchecked) {
        channel.checked = false;
      } else {
        channel.checked = true;
      }

      this.updateNotificationsSettingsArray();

      const payload = this.buildSettingsUpdatePayload(
        notification.notification_type_id
      );
      this.saveSettings(payload);
    }, 300);
  }

  toggleChannel(event: any, channel: any, notification: any) {
    event.stopPropagation();
    setTimeout(() => {
      channel.timeRangeForms.forEach((form) => {
        form.dayForm.controls.checked.patchValue(channel.checked);
        form.dayForm.controls.checked.value = channel.checked;
        form.dayForm.updateValueAndValidity();
      });
      this.updateNotificationsSettingsArray();
      const payload = this.buildSettingsUpdatePayload(
        notification.notification_type_id
      );
      this.saveSettings(payload);
    }, 300);
  }

  updateValidity() {
    this.allNotificationSettings.forEach((notification) => {
      notification.channels.forEach((channel) => {
        channel.timeRangeForms.forEach((form) => {
          form.updateValueAndValidity();
        });
      });
    });
  }

  removeTimeRange(timeRangeForm: FormArray, controlIndex: number) {
    timeRangeForm.removeAt(controlIndex);
  }

  endTimes(startTime: string) {
    if (!startTime) {return [];}

    return this.timeOpts.filter((opt) => opt.value > startTime);
  }

  addTimeRange(timeRangeForm: FormArray) {
    timeRangeForm.push(
      this.formBuilder.group({
        start_time: [''],
        end_time: [''],
      })
    );
  }
}
